import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { FundoFaixa, FundoProjetor, COLORS } from "@/components/DesafioFundo";
import useBarlow from "@/components/useBarlow";

const CONECTA = "/mascote/conecta-3d.png";
const SLIDE01 = "/treinamentos/hospitalar/slide-01.jpg";
const BARLOW = "'Barlow', sans-serif";

// TRN-2.1: mapeia o role que o menu lateral mostra embaixo do nome.
const ROLE_LABEL = { vendedor: "Vendedor", gerente: "Gerente", diretor: "Diretor", admin: "Administrador", operacional: "Operacional" };

export default function Treinamentos() {
  useBarlow();
  const navigate = useNavigate();
  const [abrindo, setAbrindo] = useState(false);
  const [treinamento, setTreinamento] = useState("Limpeza e Desinfecção Hospitalar");
  const [openCli, setOpenCli] = useState(false);
  const [carteira, setCarteira] = useState([]);
  const [loadingCart, setLoadingCart] = useState(false);
  const [busca, setBusca] = useState("");
  const [cliSel, setCliSel] = useState(null);
  const [confirmando, setConfirmando] = useState(false);
  // TRN-2: lista de produtos usados neste cliente (padrão: 3).
  const PRODUTOS_PADRAO = [
    { key: "clormix", nome: "Clormix", tipo: "Limpeza e desinfecção clorada", indicacao: "Superfícies e equipamentos" },
    { key: "wyn", nome: "Wyn Peroxy HC", tipo: "Desinfetante à base de peróxido", indicacao: "Áreas críticas e semicríticas" },
    { key: "gala", nome: "Gala Plus", tipo: "Detergente alcalino para pisos", indicacao: "Pisos e áreas extensas" },
  ];
  const [produtos, setProdutos] = useState(PRODUTOS_PADRAO.map((p) => ({ ...p })));
  const hoje = new Date().toISOString().slice(0, 10);

  const abrirSala = async () => {
    setAbrindo(true);
    try {
      const { data } = await api.post("/desafio/salas", { treinamento });
      window.open(`/desafio-projetor/${data.pin}`, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao abrir sala");
    } finally { setAbrindo(false); }
  };

  const abrirDialogHospitalar = async () => {
    setOpenCli(true); setCliSel(null); setBusca("");
    setProdutos(PRODUTOS_PADRAO.map((p) => ({ ...p })));
    if (carteira.length === 0) {
      setLoadingCart(true);
      try {
        const { data } = await api.get("/carteira");
        setCarteira(Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []));
      } catch (e) {
        toast.error("Falha ao carregar carteira");
      } finally { setLoadingCart(false); }
    }
  };

  const clientesFiltrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const norm = (c) => ({ id: c.id || c.cliente_id, nome: c.nome, nome_fantasia: c.nome_fantasia, empresa: c.empresa, razao_social: c.razao_social, cnpj: c.cnpj });
    const base = carteira.map(norm).filter((c) => c.id);
    if (!q) return base.slice(0, 60);
    return base.filter((c) => {
      const t = `${c.nome || ""} ${c.empresa || ""} ${c.razao_social || ""} ${c.nome_fantasia || ""} ${c.cnpj || ""}`.toLowerCase();
      return t.includes(q);
    }).slice(0, 60);
  }, [carteira, busca]);

  const confirmarAbrirApresentacao = async () => {
    if (!cliSel) { toast.error("Escolha o cliente"); return; }
    const prodsLimpos = produtos.filter((p) => (p.nome || "").trim()).map((p) => ({
      key: p.key || "", nome: p.nome.trim(), tipo: (p.tipo || "").trim(), indicacao: (p.indicacao || "").trim(),
    }));
    if (prodsLimpos.length === 0) { toast.error("Informe ao menos 1 produto"); return; }
    setConfirmando(true);
    try {
      // TRN-2.1: pega cargo do usuário logado (fallback do menu lateral) p/ gravar assinatura.
      let cargoLabel = "";
      try {
        const me = await api.get("/auth/me");
        const u = me?.data || {};
        cargoLabel = u.cargo || u.role_label || ROLE_LABEL[(u.role || "").toLowerCase()] || "";
      } catch (_) { /* ignora */ }
      const { data } = await api.post("/canal-cliente/treinamentos-aplicados", {
        cliente_id: cliSel.id,
        tema: "Limpeza e Desinfecção Hospitalar",
        data: hoje,
        produtos: prodsLimpos,
      });
      const apid = data?.aplicacao?.id;
      const token = data?.token;
      if (!apid || !token) throw new Error("Resposta inválida");
      setOpenCli(false);
      navigate(`/treinamento/hospitalar/${apid}`, {
        state: { token, clienteNome: cliSel.nome_fantasia || cliSel.empresa || cliSel.nome, apresentacao: data.aplicacao, cargoLabel },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao registrar treinamento");
    } finally { setConfirmando(false); }
  };

  return (
    <div
      style={{ fontFamily: BARLOW, position: "relative", background: COLORS.navy, overflow: "hidden", minHeight: "100dvh" }}
      className="-mx-4 md:-mx-8 -mt-6 md:-mt-8 -mb-20 md:-mb-8"
    >
      {/* CANAL-3.1 / PAINEL-1.8 (item 7): fundo do Desafio cobrindo a área
          inteira de conteúdo (sem faixas brancas em volta nem embaixo). */}
      <FundoProjetor />
      <div
        className="p-6 md:p-8 max-w-6xl mx-auto"
        style={{ position: "relative", zIndex: 2, paddingBottom: "calc(6rem + env(safe-area-inset-bottom))" }}
      >
        {/* Cabeçalho direto sobre o fundo navy, sem caixa em volta */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ color: COLORS.accent, fontSize: 12, fontWeight: 700, letterSpacing: 2 }}>TREINAMENTOS · FERRAMENTAS AO VIVO</div>
          <h1 style={{ color: "#fff", fontSize: 36, fontWeight: 800, margin: "6px 0 6px" }}>Treinamentos</h1>
          <p style={{ color: COLORS.textSec, fontSize: 15, margin: 0, maxWidth: 540 }}>Ferramentas de treinamento ao vivo para a equipe de higienização dos nossos clientes.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Card Desafio MPires */}
        <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,20,35,0.06)", border: "1px solid rgba(0,20,35,0.05)", display: "flex", flexDirection: "column" }} data-testid="card-treinamento-desafio">
          <div style={{ background: COLORS.navy, position: "relative", overflow: "hidden", padding: 24, display: "flex", alignItems: "center", justifyContent: "center", height: 198 }}>
            <FundoFaixa />
            <img src={CONECTA} alt="Conecta" style={{ height: 150, position: "relative", zIndex: 2 }} />
          </div>
          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ color: COLORS.navy, fontSize: 22, fontWeight: 700 }}>Desafio MPires</div>
            <div style={{ color: "#4A6478", fontSize: 14, marginTop: 4 }}>Jogo ao vivo em grupo: PIN no projetor, participantes pelo celular, perguntas e pódio.</div>
            <div style={{ marginTop: 16 }}>
              <label style={{ display: "block", fontSize: 11, color: "#4A6478", fontWeight: 600, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Treinamento</label>
              <select value={treinamento} onChange={(e) => setTreinamento(e.target.value)} data-testid="select-treinamento"
                style={{ width: "100%", height: 44, padding: "0 12px", borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", fontSize: 14, fontFamily: BARLOW }}>
                <option>Limpeza e Desinfecção Hospitalar</option>
              </select>
            </div>
            <button onClick={abrirSala} disabled={abrindo} data-testid="btn-abrir-sala"
              style={{ marginTop: 16, width: "100%", height: 50, borderRadius: 12, border: 0, background: COLORS.navy, color: "#fff", fontSize: 15, fontWeight: 800, fontFamily: BARLOW, cursor: abrindo ? "default" : "pointer", opacity: abrindo ? 0.6 : 1 }}>
              {abrindo ? "Abrindo…" : "Abrir sala"}
            </button>
          </div>
        </div>

        {/* Card Treinamento Hospitalar */}
        <div style={{ background: "#fff", borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,20,35,0.06)", border: "1px solid rgba(0,20,35,0.05)", display: "flex", flexDirection: "column" }} data-testid="card-treinamento-hospitalar">
          <div style={{ background: COLORS.navy, position: "relative", overflow: "hidden", height: 198 }}>
            <img src={SLIDE01} alt="Capa Limpeza e Desinfecção Hospitalar" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ color: COLORS.navy, fontSize: 22, fontWeight: 700 }}>Treinamento Hospitalar</div>
            <div style={{ color: "#4A6478", fontSize: 14, marginTop: 4 }}>Limpeza e Desinfecção Hospitalar · 23 slides</div>
            <button onClick={abrirDialogHospitalar} data-testid="btn-abrir-hospitalar"
              style={{ marginTop: 16, width: "100%", height: 50, borderRadius: 12, border: 0, background: COLORS.navy, color: "#fff", fontSize: 15, fontWeight: 800, fontFamily: BARLOW, cursor: "pointer" }}>
              Abrir treinamento
            </button>
          </div>
        </div>
      </div>
      </div>

      {/* Dialog: escolha de cliente (TRN-2.1: portal + layout fixo com rolagem interna) */}
      {openCli && createPortal(
        <div onClick={() => !confirmando && setOpenCli(false)} data-testid="dialog-escolher-cliente"
          style={{ position: "fixed", inset: 0, background: "rgba(0,20,35,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000, padding: 16, fontFamily: BARLOW, boxSizing: "border-box" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 520, maxHeight: "calc(100dvh - 32px)", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", overflow: "hidden" }}>
            {/* TOPO FIXO */}
            <div style={{ padding: "18px 20px 12px", borderBottom: "1px solid rgba(0,62,98,0.08)" }}>
              <div style={{ color: COLORS.navy, fontSize: 20, fontWeight: 800 }}>Para qual cliente é este treinamento?</div>
              <div style={{ color: "#4A6478", fontSize: 13, marginTop: 4 }}>Data: {hoje.split("-").reverse().join("/")}</div>
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar cliente…" autoFocus data-testid="busca-cliente-trein"
                style={{ marginTop: 12, width: "100%", height: 42, padding: "0 12px", borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", fontSize: 14, fontFamily: BARLOW, boxSizing: "border-box" }} />
            </div>
            {/* MEIO COM ROLAGEM INTERNA */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "12px 20px 16px" }}>
              <div style={{ border: "1px solid rgba(0,62,98,0.12)", borderRadius: 10, maxHeight: 260, overflowY: "auto" }}>
                {loadingCart ? (
                  <div style={{ padding: 14, textAlign: "center", color: "#4A6478", fontSize: 13 }}>Carregando carteira…</div>
                ) : clientesFiltrados.length === 0 ? (
                  <div style={{ padding: 14, textAlign: "center", color: "#4A6478", fontSize: 13 }}>Nenhum cliente encontrado.</div>
                ) : clientesFiltrados.map((c) => {
                  const sel = cliSel?.id === c.id;
                  const label = c.nome_fantasia || c.empresa || c.nome || "(sem nome)";
                  const sub = c.cnpj ? `CNPJ ${c.cnpj}` : (c.nome && c.nome !== label ? c.nome : "");
                  return (
                    <div key={c.id} onClick={() => setCliSel(c)} data-testid={`cli-opt-${c.id}`}
                      style={{ padding: "10px 12px", cursor: "pointer", background: sel ? "rgba(0,138,176,0.12)" : "transparent", borderBottom: "1px solid rgba(0,62,98,0.06)" }}>
                      <div style={{ color: COLORS.navy, fontSize: 14, fontWeight: 700 }}>{label}</div>
                      {sub && <div style={{ color: "#4A6478", fontSize: 12 }}>{sub}</div>}
                    </div>
                  );
                })}
              </div>

              {/* Produtos usados neste cliente */}
              <div style={{ marginTop: 16, color: COLORS.navy, fontSize: 15, fontWeight: 800 }}>Produtos usados neste cliente</div>
              <div style={{ color: "#4A6478", fontSize: 12, marginTop: 2, marginBottom: 8 }}>Aparecem no slide Produtos da apresentação.</div>
              {produtos.map((p, i) => {
                const setP = (patch) => setProdutos((arr) => arr.map((x, idx) => idx === i ? { ...x, ...patch } : x));
                const onSelect = (val) => {
                  if (val === "outro") setP({ key: "outro", nome: "", tipo: "", indicacao: "" });
                  else {
                    const base = PRODUTOS_PADRAO.find((pp) => pp.key === val);
                    if (base) setP({ ...base });
                  }
                };
                return (
                  <div key={i} data-testid={`prod-row-${i}`} style={{ marginBottom: 10, padding: 10, borderRadius: 12, border: "1px solid rgba(0,62,98,0.14)" }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select value={p.key || "outro"} onChange={(e) => onSelect(e.target.value)} data-testid={`prod-sel-${i}`}
                        style={{ flex: 1, height: 40, padding: "0 10px", borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", fontSize: 14, fontFamily: BARLOW }}>
                        <option value="clormix">Clormix</option>
                        <option value="wyn">Wyn Peroxy HC</option>
                        <option value="gala">Gala Plus</option>
                        <option value="outro">Outro produto</option>
                      </select>
                      {produtos.length > 1 && (
                        <button onClick={() => setProdutos((arr) => arr.filter((_, idx) => idx !== i))} data-testid={`prod-rem-${i}`}
                          style={{ height: 40, width: 40, borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", color: "#B23A3A", fontSize: 18, fontWeight: 800, cursor: "pointer" }} aria-label="Remover produto">×</button>
                      )}
                    </div>
                    {p.key === "outro" && (
                      <input value={p.nome} onChange={(e) => setP({ nome: e.target.value.slice(0, 24) })} placeholder="Nome do produto" data-testid={`prod-nome-${i}`}
                        style={{ marginTop: 8, width: "100%", height: 40, padding: "0 10px", borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", fontSize: 14, fontFamily: BARLOW, boxSizing: "border-box" }} />
                    )}
                    <input value={p.tipo} onChange={(e) => setP({ tipo: e.target.value.slice(0, 40) })} placeholder="Tipo (ex.: Desinfetante à base de peróxido)" data-testid={`prod-tipo-${i}`}
                      style={{ marginTop: 8, width: "100%", height: 40, padding: "0 10px", borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", fontSize: 14, fontFamily: BARLOW, boxSizing: "border-box" }} />
                    <input value={p.indicacao} onChange={(e) => setP({ indicacao: e.target.value.slice(0, 50) })} placeholder="Indicação (ex.: Superfícies e equipamentos)" data-testid={`prod-ind-${i}`}
                      style={{ marginTop: 8, width: "100%", height: 40, padding: "0 10px", borderRadius: 10, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", fontSize: 14, fontFamily: BARLOW, boxSizing: "border-box" }} />
                  </div>
                );
              })}
              {produtos.length < 4 && (
                <button onClick={() => setProdutos((arr) => [...arr, { key: "outro", nome: "", tipo: "", indicacao: "" }])} data-testid="prod-add"
                  style={{ width: "100%", height: 40, borderRadius: 10, border: "1.5px dashed rgba(0,62,98,0.4)", background: "#fff", color: COLORS.navy, fontSize: 13, fontWeight: 700, fontFamily: BARLOW, cursor: "pointer", marginTop: 2 }}>
                  + Adicionar produto
                </button>
              )}
            </div>
            {/* RODAPÉ FIXO */}
            <div style={{ display: "flex", gap: 10, padding: "12px 20px", borderTop: "1px solid rgba(0,62,98,0.08)", background: "#fff" }}>
              <button onClick={() => setOpenCli(false)} disabled={confirmando} data-testid="btn-cancelar-trein"
                style={{ flex: 1, height: 46, borderRadius: 12, border: "1.5px solid rgba(0,62,98,0.28)", background: "#fff", color: COLORS.navy, fontSize: 14, fontWeight: 700, fontFamily: BARLOW, cursor: "pointer" }}>
                Cancelar
              </button>
              <button onClick={confirmarAbrirApresentacao} disabled={!cliSel || confirmando} data-testid="btn-confirmar-trein"
                style={{ flex: 1, height: 46, borderRadius: 12, border: 0, background: COLORS.navy, color: "#fff", fontSize: 14, fontWeight: 800, fontFamily: BARLOW, cursor: (!cliSel || confirmando) ? "default" : "pointer", opacity: (!cliSel || confirmando) ? 0.5 : 1 }}>
                {confirmando ? "Abrindo…" : "Confirmar"}
              </button>
            </div>
          </div>
        </div>, document.body)}
    </div>
  );
}
